import { Directive, ElementRef, HostListener, inject, input } from '@angular/core';
import { take, tap } from 'rxjs/operators';
import { assert } from '../../../core/assert/assert';
import { AbsencePlannerEventModel } from '../../../core/models/absence-planner/absence-planner-event.model';
import { UserModel } from '../../../core/models/user/user.model';
import { ShiveDialogService } from '../../../core/services/controls/shive-dialog.service';
import { ShiveRenderService } from '../../../core/services/rendering/shive-render.service';
import { TouchAndMouseHandlerService } from '../../../core/services/touch-and-mouse-handler.service';
import {
  FixInterval,
  TimeBlockType,
} from '../../calendar/components/time-block/types/time-block-types';
import { DateTimeHelper } from '../../calendar/util/date-time-helper';
import { AbsencePlannerEventDialogComponent } from '../dialogs/absence-planner-event-dialog/absence-planner-event-dialog.component';
import { AbsencePlannerService } from '../services/absence-planner.service';
import {
  ActiveSlotCSSClass,
  SelectedLeftPlannerEvent,
  SelectedRightPlannerEvent,
  SingleActiveSlotCSSClass,
} from '../utils/absence-planner.constants';

@Directive({
  selector: '[appPlannerEventsContainer]',
  standalone: true,
})
export class AbsencePlannerEventsContainerDirective {
  date = input.required<Date>();
  member = input.required<UserModel>();
  public readonly elementRef = inject<ElementRef<HTMLDivElement>>(ElementRef);

  private static startDate: Date = null;
  private static currentDate: Date = null;
  private static userId: number = null;
  private addEventInProgress = false;
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly absencePlannerService = inject(AbsencePlannerService);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly touchAndMouseHandlerService = inject(TouchAndMouseHandlerService);

  constructor() {
    this.initEvents();
  }

  @HostListener('mousedown')
  public mouseDown(): void {
    AbsencePlannerEventsContainerDirective.startDate = this.date();
    AbsencePlannerEventsContainerDirective.currentDate = this.date();
    AbsencePlannerEventsContainerDirective.userId = this.member().id;
    this.addEventInProgress = true;

    this.shiveRenderService.addClass(this.elementRef.nativeElement, ActiveSlotCSSClass);
    this.shiveRenderService.addClass(this.elementRef.nativeElement, SingleActiveSlotCSSClass);
    this.absencePlannerService.createEventInteractionInProgress$.next(true);
  }

  @HostListener('mousemove')
  public mouseMove(): void {
    if (
      !DateTimeHelper.isSameDay(AbsencePlannerEventsContainerDirective.currentDate, this.date()) &&
      AbsencePlannerEventsContainerDirective.startDate
    ) {
      this.removeCSSClasses();
      AbsencePlannerEventsContainerDirective.currentDate = this.date();
      this.setSelectedCSSClasses(AbsencePlannerEventsContainerDirective.currentDate);
      this.connectPlannerEventParts(AbsencePlannerEventsContainerDirective.currentDate);
    }
  }

  private removeCSSClasses(): void {
    const plannerEventSlots = this.absencePlannerService.plannerModel.plannerEventsContainer.get(
      AbsencePlannerEventsContainerDirective.userId,
    );
    plannerEventSlots.forEach((plannerEventSlot) => {
      this.shiveRenderService.removeClass(
        plannerEventSlot.elementRef.nativeElement,
        `${ActiveSlotCSSClass}`,
      );

      this.shiveRenderService.removeClass(
        plannerEventSlot.elementRef.nativeElement,
        `${SelectedLeftPlannerEvent}`,
      );

      this.shiveRenderService.removeClass(
        plannerEventSlot.elementRef.nativeElement,
        `${SelectedRightPlannerEvent}`,
      );

      this.shiveRenderService.removeClass(
        plannerEventSlot.elementRef.nativeElement,
        `${SingleActiveSlotCSSClass}`,
      );
    });
  }

  private connectPlannerEventParts(currentDate: Date): void {
    const startDate = AbsencePlannerEventsContainerDirective.startDate;
    const weekDays = DateTimeHelper.eachDayOfInterval(startDate, currentDate, false);
    const plannerEventSlots = this.absencePlannerService.plannerModel.plannerEventsContainer.get(
      AbsencePlannerEventsContainerDirective.userId,
    );

    assert(plannerEventSlots.length > 0, 'Planner event slots are empty.');

    weekDays.forEach((weekDay) => {
      const plannerEventSlot = plannerEventSlots.find((slot) =>
        DateTimeHelper.isSameDay(slot.date(), weekDay),
      );
      assert(plannerEventSlot !== null, 'Planner event slot is null.');

      // Add class to the planner event slot to indicate it as active.
      this.shiveRenderService.addClass(
        plannerEventSlot.elementRef.nativeElement,
        ActiveSlotCSSClass,
      );

      // We only have one selected slot
      if (weekDays.length === 1) {
        this.shiveRenderService.addClass(
          plannerEventSlot.elementRef.nativeElement,
          SingleActiveSlotCSSClass,
        );
      }
    });
  }

  private setSelectedCSSClasses(currentDate: Date): void {
    const plannerEventSlots = this.absencePlannerService.plannerModel.plannerEventsContainer.get(
      AbsencePlannerEventsContainerDirective.userId,
    );

    const originSlot = plannerEventSlots.find((slot) =>
      DateTimeHelper.isSameDay(slot.date(), AbsencePlannerEventsContainerDirective.startDate),
    );
    const lastSlot = plannerEventSlots.find((slot) =>
      DateTimeHelper.isSameDay(slot.date(), currentDate),
    );

    // The original clicked slot is left of the current slot
    if (DateTimeHelper.isBefore(originSlot.date(), lastSlot.date())) {
      this.shiveRenderService.addClass(
        originSlot.elementRef.nativeElement,
        SelectedLeftPlannerEvent,
      );

      this.shiveRenderService.addClass(
        lastSlot.elementRef.nativeElement,
        SelectedRightPlannerEvent,
      );
      return;
    }

    // The original clicked slot is right of the current slot
    if (DateTimeHelper.isAfter(originSlot.date(), lastSlot.date())) {
      this.shiveRenderService.addClass(
        originSlot.elementRef.nativeElement,
        SelectedRightPlannerEvent,
      );

      this.shiveRenderService.addClass(lastSlot.elementRef.nativeElement, SelectedLeftPlannerEvent);
      return;
    }
  }

  private initEvents() {
    // This event gets called multiple times, so prevent adding multiple planner events.
    this.touchAndMouseHandlerService.globalMouseReleased$.subscribe(() => {
      if (!this.addEventInProgress) {
        return;
      }
      this.addEventInProgress = false;

      const newPlannerEventModel = new AbsencePlannerEventModel();
      const start = DateTimeHelper.isBefore(
        AbsencePlannerEventsContainerDirective.startDate,
        AbsencePlannerEventsContainerDirective.currentDate,
      )
        ? AbsencePlannerEventsContainerDirective.startDate
        : AbsencePlannerEventsContainerDirective.currentDate;
      const end = DateTimeHelper.isBefore(
        AbsencePlannerEventsContainerDirective.startDate,
        AbsencePlannerEventsContainerDirective.currentDate,
      )
        ? AbsencePlannerEventsContainerDirective.currentDate
        : AbsencePlannerEventsContainerDirective.startDate;

      const plannerModel = this.absencePlannerService.plannerModel;
      newPlannerEventModel.start = DateTimeHelper.mergeDateAndTime(
        start,
        plannerModel.workTimeStart,
      );
      newPlannerEventModel.end = DateTimeHelper.mergeDateAndTime(end, plannerModel.workTimeEnd);
      newPlannerEventModel.userId = AbsencePlannerEventsContainerDirective.userId;
      newPlannerEventModel.fixInterval = FixInterval.FullWorkingDay;
      newPlannerEventModel.type = TimeBlockType.NonExistingBlock;

      // Open "Add event" dialog.
      const ref = this.shiveDialogService.open({
        content: AbsencePlannerEventDialogComponent,
        width: 572,
        title: 'Event hinzufügen',
      });
      ref.content.setInput('plannerEventModel', newPlannerEventModel);

      ref.result
        .pipe(
          take(1),
          tap(() => {
            // Reset
            this.removeCSSClasses();
            AbsencePlannerEventsContainerDirective.startDate = null;
            AbsencePlannerEventsContainerDirective.userId = null;
            this.absencePlannerService.createEventInteractionInProgress$.next(false);
          }),
        )
        .subscribe();
    });
  }
}
